import { useRef, useState } from "react";
import { INK, TEXT, MUTED, LINE, NEUTRAL_BG, ARCH_QUAD_FILLS, ARCH_COLORS, CULTURE_COLORS, SURFACE, text, WHITE, FAINT } from "./constants";
import { useIsMobile } from "./lib/responsive";
import { HoverCard } from "./shared";
import { CULTURE_DIMS, CULTURE_DIM_INFO, ARCHETYPES, cultureQuestions, cultureScores, cultureAxes, cultureBlend } from "./lib/culture";

// Perfil de cultura (30/10): las 6 dimensiones en barras 1–5 y, solo para el
// admin (`showMap`), la posición de la empresa en el mapa de Archetypes. El
// cliente ve solo las barras (31/10). Solo pinta: el cálculo vive en
// lib/culture.js. Sin respuestas v2, no pinta nada.
// Barras en pastel, una por dimensión (CULTURE_COLORS, 31/10), la misma familia
// que el resultado de Archetypes; riel NEUTRAL_BG. Al pasar el ratón (o tocar)
// explican la dimensión; no navegan, así que nada del azul de marca.
// `compact` = versión de la Home: nombre y cifra en una línea y barra fina,
// sin los extremos de la escala.

// Qué significa una dimensión (31/10): lo que sale al pasar el ratón por su
// barra (al tocarla, en el móvil). Qué mide, cómo se nota una nota baja y una
// alta, qué candidato encaja y de qué preguntas sale. Textos en CULTURE_DIM_INFO.
// `bare` (30/10): sin la línea de nombre y cifra, que la caja del Perfil lleva en su cabecera.
const DimDetail = ({ dim, score, lang, bare = false, mobile = false }) => {
  const es = lang === "es";
  const info = CULTURE_DIM_INFO[dim.id];
  const L = (o) => (es ? o.es : o.en);
  const reading = score <= 2.2
    ? (es ? `Tira hacia «${dim.low.es}»` : `Leans to “${dim.low.en}”`)
    : score >= 3.8
      ? (es ? `Tira hacia «${dim.high.es}»` : `Leans to “${dim.high.en}”`)
      : (es ? "Equilibrio entre los dos extremos" : "Balanced between both ends");
  const from = cultureQuestions(lang).filter(q => q.dim === dim.id).map(q => q.title).join(" · ");
  // Mismo formato que todas las ventanitas (31/10): nombre 13/800 y cifra,
  // una línea gris, el texto en `text.body` y apartados con `text.label` corto
  const block = (label, body) => (
    <div style={{ marginTop: 10 }}>
      <div style={{ ...text.label, marginBottom: 2 }}>{label}</div>
      <div style={text.bodySm}>{body}</div>
    </div>
  );
  return (
    <div>
      {!bare && <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: INK }}>{L(dim)}</span>
        <span style={{ fontSize: 13, fontWeight: 800, color: INK, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{score.toFixed(1)}<span style={{ ...text.meta }}> / 5</span></span>
      </div>}
      <div style={{ ...text.cardSub, marginTop: bare ? 0 : 2 }}>{reading}</div>
      <p style={{ ...text.body, margin: "8px 0 0" }}>{L(info.about)}</p>
      {/* En la caja del Perfil (ancha), nota baja y alta lado a lado */}
      <div style={bare && !mobile ? { display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 14 } : null}>
        {block(es ? "Nota baja" : "Low score", L(info.lowEx))}
        {block(es ? "Nota alta" : "High score", L(info.highEx))}
      </div>
      {block(es ? "Encaje" : "Fit", L(info.fit))}
      <div style={{ ...text.meta, marginTop: 10, lineHeight: 1.5 }}>
        {es ? "Sale de: " : "Based on: "}{from}. {es ? "Ningún extremo es mejor." : "Neither end is better."}
      </div>
    </div>
  );
};

const Bar = ({ dim, score, lang, compact, open, onEnter, onLeave, onTap, mobile, rowRef }) => {
  const es = lang === "es";
  const pct = ((score - 1) / 4) * 100;
  return (
    <div ref={rowRef} role="button" tabIndex={0} aria-expanded={open}
      onMouseEnter={mobile ? undefined : onEnter} onMouseLeave={mobile ? undefined : onLeave}
      onFocus={mobile ? undefined : onEnter} onBlur={mobile ? undefined : onLeave}
      onClick={mobile ? onTap : undefined} onKeyDown={e => { if (e.key === "Enter" && mobile) onTap(); }}
      style={{ marginBottom: compact ? 10 : 14, cursor: "pointer", borderRadius: 8, outline: "none" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, marginBottom: compact ? 4 : 6 }}>
        <span style={{ fontSize: compact ? 12 : 13, fontWeight: compact ? 400 : 700, color: compact ? TEXT : INK, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{es ? dim.es : dim.en}</span>
        <span style={{ fontSize: compact ? 12 : 13, fontWeight: 700, color: INK, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{score.toFixed(1)}</span>
      </div>
      <div style={{ position: "relative", height: compact ? 6 : 8, background: NEUTRAL_BG, borderRadius: 4 }}>
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${pct}%`, minWidth: 4, background: CULTURE_COLORS[dim.id] || TEXT, borderRadius: 4 }} />
      </div>
      {!compact && (
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 4 }}>
          <span style={{ fontSize: 11, color: MUTED }}>{es ? dim.low.es : dim.low.en}</span>
          <span style={{ fontSize: 11, color: MUTED, textAlign: "right" }}>{es ? dim.high.es : dim.high.en}</span>
        </div>
      )}
      {mobile && open && (
        <div style={{ marginTop: 10, padding: 14, border: `1.5px solid ${LINE}`, borderRadius: 12, background: WHITE }}>
          <DimDetail dim={dim} score={score} lang={lang} />
        </div>
      )}
    </div>
  );
};

// Mapa de los dos ejes de Archetypes: x = tarea → personas, y = estructura → velocidad.
// SOLO ADMIN: el cliente no lo ve (decisión de Borja, 31/10).
const CultureMap = ({ axes, blend, lang }) => {
  const es = lang === "es";
  const S = 200, pad = 10, half = S / 2;
  const cx = half + axes.x * (half - pad), cy = half - axes.y * (half - pad);
  const top = blend.indexOf(Math.max(...blend));
  const corner = [
    { x: 8, y: S - 8, anchor: "start" },   // Arquitecto: tarea + estructura
    { x: 8, y: 16, anchor: "start" },       // Catalizador: tarea + velocidad
    { x: S - 8, y: 16, anchor: "end" },     // Conector: personas + velocidad
    { x: S - 8, y: S - 8, anchor: "end" },  // Guardián: personas + estructura
  ];
  return (
    <div>
      <svg viewBox={`0 0 ${S} ${S}`} width="100%" style={{ maxWidth: 240, display: "block" }} role="img"
        aria-label={es ? `Posición de la cultura: ${ARCHETYPES[top].es}` : `Culture position: ${ARCHETYPES[top].en}`}>
        {/* Cuadrantes en los pastel del mapa de Archetypes (31/10) */}
        {[[0, half], [0, 0], [half, 0], [half, half]].map(([qx, qy], i) => (
          <rect key={i} x={qx + 1.5} y={qy + 1.5} width={half - 3} height={half - 3} rx="8" fill={ARCH_QUAD_FILLS[i]} />
        ))}
        {ARCHETYPES.map((a, i) => (
          <text key={a.es} x={corner[i].x} y={corner[i].y} textAnchor={corner[i].anchor}
            style={{ fontSize: 11, fontWeight: i === top ? 800 : 600, fill: i === top ? a.color : FAINT }}>
            {es ? a.es : a.en}
          </text>
        ))}
        <circle cx={cx} cy={cy} r="7" fill={ARCHETYPES[top].color} stroke={WHITE} strokeWidth="2.5" />
      </svg>
      <div style={{ display: "flex", justifyContent: "space-between", maxWidth: 240, marginTop: 4 }}>
        <span style={{ fontSize: 11, color: MUTED }}>{es ? "← Tarea" : "← Task"}</span>
        <span style={{ fontSize: 11, color: MUTED }}>{es ? "Personas →" : "People →"}</span>
      </div>
      <div style={{ fontSize: 12, color: TEXT, lineHeight: 1.6, marginTop: 8, maxWidth: 240 }}>
        {es
          ? <>Cultura más cercana al estilo <b style={{ color: INK }}>{ARCHETYPES[top].es}</b> ({blend[top]} %). Arriba, velocidad; abajo, estructura.</>
          : <>Culture closest to the <b style={{ color: INK }}>{ARCHETYPES[top].en}</b> style ({blend[top]}%). Top is speed; bottom is structure.</>}
      </div>
    </div>
  );
};

// Hexágono (30/10, Home del cliente): las 6 dimensiones como las stats de un
// jugador. Anillos de 1 a 5 (el centro es 1), un solo relleno pastel con el
// borde en el violeta de Archetypes y, en cada vértice, la nota y un nombre
// corto. Cada vértice es la pieza que abre la explicación (como las barras).
const SHORT = {
  speed: { es: "Velocidad", en: "Speed" }, autonomy: { es: "Autonomía", en: "Autonomy" },
  standards: { es: "Exigencia", en: "Standards" }, growth: { es: "Personas", en: "People dev." },
  candor: { es: "Franqueza", en: "Candor" }, risk: { es: "Largo plazo", en: "Long term" },
};
const CultureRadar = ({ dims, scores, lang, mobile, openDim, onEnter, onLeave, onTap, labelRef }) => {
  const es = lang === "es";
  const S = 240, c = S / 2, R = 78, PX = 44; // PX: margen lateral para las etiquetas
  const n = dims.length;
  const pt = (i, r) => { const a = -Math.PI / 2 + (i * 2 * Math.PI) / n; return [c + r * Math.cos(a), c + r * Math.sin(a)]; };
  const ring = (r) => dims.map((_, i) => pt(i, r).map(v => v.toFixed(1)).join(",")).join(" ");
  const val = dims.map((d, i) => pt(i, (R * (scores[d.id] - 1)) / 4));
  return (
    <svg viewBox={`${-PX} 0 ${S + 2 * PX} ${S}`} width="100%" style={{ maxWidth: S + 2 * PX, display: "block", margin: "0 auto", overflow: "visible" }} role="img"
      aria-label={dims.map(d => `${es ? d.es : d.en} ${scores[d.id].toFixed(1)}`).join(", ")}>
      {[5, 4, 3, 2].map(k => <polygon key={k} points={ring((R * (k - 1)) / 4)} fill={k === 5 ? SURFACE : "none"} stroke={LINE} strokeWidth="1" />)}
      {dims.map((_, i) => { const [x, y] = pt(i, R); return <line key={i} x1={c} y1={c} x2={x} y2={y} stroke={LINE} />; })}
      <polygon points={val.map(p => p.map(v => v.toFixed(1)).join(",")).join(" ")} fill={CULTURE_COLORS.speed} fillOpacity="0.6"
        stroke={ARCH_COLORS[1]} strokeWidth="2" strokeLinejoin="round" />
      {val.map(([x, y], i) => <circle key={i} cx={x} cy={y} r={openDim === dims[i].id ? 4.5 : 3} fill={ARCH_COLORS[1]} />)}
      {dims.map((d, i) => {
        const [x, y] = pt(i, R + 20);
        const anchor = Math.abs(x - c) < 4 ? "middle" : x > c ? "start" : "end";
        const dy = y < c - 4 ? -4 : y > c + 4 ? 8 : 2;
        return (
          <g key={d.id} ref={(node) => labelRef(d.id, node)} role="button" tabIndex={0} aria-expanded={openDim === d.id}
            onMouseEnter={mobile ? undefined : () => onEnter(d.id)} onMouseLeave={mobile ? undefined : () => onLeave(d.id)}
            onFocus={mobile ? undefined : () => onEnter(d.id)} onBlur={mobile ? undefined : () => onLeave(d.id)}
            onClick={mobile ? () => onTap(d.id) : undefined} onKeyDown={e => { if (e.key === "Enter" && mobile) onTap(d.id); }}
            style={{ cursor: "pointer", outline: "none" }}>
            <text x={x} y={y + dy - 7} textAnchor={anchor} style={{ fontSize: 15, fontWeight: 800, fill: INK, fontVariantNumeric: "tabular-nums" }}>{scores[d.id].toFixed(1)}</text>
            <text x={x} y={y + dy + 7} textAnchor={anchor} style={{ fontSize: 11, fill: openDim === d.id ? INK : MUTED }}>{es ? SHORT[d.id].es : SHORT[d.id].en}</text>
          </g>
        );
      })}
    </svg>
  );
};

// Perfil completo (30/10, pestaña Perfil de «Documento de cultura»): el hexágono
// en el centro y alrededor una caja por dimensión, con la cabecera en su color y
// dentro lo mismo que la ventanita de las barras. Cada caja va al lado de su
// vértice (derecha: velocidad, autonomía, exigencia; izquierda: largo plazo,
// franqueza, personas). Pasar el ratón por una caja marca su vértice, y al revés.
const DimBox = ({ dim, score, lang, hot, onEnter, onLeave, mobile }) => (
  <div onMouseEnter={onEnter} onMouseLeave={onLeave}
    style={{ border: `1.5px solid ${hot ? CULTURE_COLORS[dim.id] : LINE}`, borderRadius: 12, overflow: "hidden", background: WHITE, transition: "border-color 0.15s" }}>
    <div style={{ background: CULTURE_COLORS[dim.id], padding: "9px 14px", display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
      <span style={{ fontSize: 13, fontWeight: 800, color: INK }}>{lang === "es" ? dim.es : dim.en}</span>
      <span style={{ fontSize: 15, fontWeight: 800, color: INK, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{score.toFixed(1)}<span style={{ fontSize: 11, fontWeight: 600 }}> / 5</span></span>
    </div>
    <div style={{ padding: "12px 14px 14px" }}>
      <DimDetail dim={dim} score={score} lang={lang} bare mobile={mobile} />
    </div>
  </div>
);

export const CultureProfileBoard = ({ answers, lang, showMap = false }) => {
  const isMobile = useIsMobile();
  const [hot, setHot] = useState(null);
  const scores = cultureScores(answers);
  const dims = CULTURE_DIMS.filter((d) => scores[d.id] != null);
  if (!dims.length) return null;
  const byId = Object.fromEntries(dims.map(d => [d.id, d]));
  const off = (id) => setHot(h => (h === id ? null : h));
  const box = (id) => byId[id] && <DimBox key={id} dim={byId[id]} score={scores[id]} lang={lang} hot={hot === id} onEnter={() => setHot(id)} onLeave={() => off(id)} mobile={isMobile} />;
  const col = (ids) => <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>{ids.map(box)}</div>;
  const radar = (
    <CultureRadar dims={dims} scores={scores} lang={lang} mobile={isMobile} openDim={hot} labelRef={() => {}}
      onEnter={setHot} onLeave={off} onTap={(id) => setHot(h => (h === id ? null : id))} />
  );
  const axes = showMap ? cultureAxes(scores) : null;
  return (
    <div>
      {isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {radar}
          {col(["speed", "autonomy", "standards", "growth", "candor", "risk"])}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 340px minmax(0, 1fr)", gap: 20, alignItems: "start" }}>
          {col(["risk", "candor", "growth"])}
          {/* El hexágono se queda a la vista al bajar por las cajas */}
          <div style={{ position: "sticky", top: 24, paddingTop: 40 }}>{radar}</div>
          {col(["speed", "autonomy", "standards"])}
        </div>
      )}
      {axes && (
        <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${LINE}` }}>
          <div style={{ ...text.label, marginBottom: 10 }}>{lang === "es" ? "Mapa de Archetypes · solo admin" : "Archetypes map · admin only"}</div>
          <CultureMap axes={axes} blend={cultureBlend(axes)} lang={lang} />
        </div>
      )}
    </div>
  );
};

// ¿Hay algo que pintar? (respuestas v2 con al menos una dimensión)
export const hasCultureProfile = (answers) => Object.values(cultureScores(answers)).some((v) => v != null);

// `radar` (30/10): el hexágono en vez de las barras (Home del cliente).
const CultureProfile = ({ answers, lang, style, showMap = false, compact = false, title = true, radar = false }) => {
  const isMobile = useIsMobile();
  // Dimensión abierta. Escritorio: al pasar el ratón, con un respiro al salir
  // para poder entrar en la ventanita. Móvil: al tocar, debajo de la barra.
  const [openDim, setOpenDim] = useState(null);
  const closeT = useRef(null);
  const rows = useRef({});
  const scores = cultureScores(answers);
  const dims = CULTURE_DIMS.filter((d) => scores[d.id] != null);
  if (!dims.length) return null;
  const axes = showMap && !compact ? cultureAxes(scores) : null;
  const blend = axes ? cultureBlend(axes) : null;
  const es = lang === "es";
  const enter = (id) => { clearTimeout(closeT.current); setOpenDim(id); };
  const leave = (id) => { clearTimeout(closeT.current); closeT.current = setTimeout(() => setOpenDim(o => (o === id ? null : o)), 160); };
  const openD = !isMobile && dims.find(d => d.id === openDim);
  return (
    <div style={{ ...style }}>
      {title && <div style={{ ...text.label, marginBottom: 4 }}>{es ? "Perfil de cultura" : "Culture profile"}</div>}
      {title && <div style={{ fontSize: 12, color: MUTED, marginBottom: 14 }}>
        {isMobile
          ? (es ? "Toca una dimensión para ver qué mide, con ejemplos." : "Tap a dimension to see what it measures, with examples.")
          : (es ? "Pasa el ratón por una dimensión para ver qué mide, con ejemplos." : "Hover over a dimension to see what it measures, with examples.")}
      </div>}
      {radar ? (
        <div>
          <CultureRadar dims={dims} scores={scores} lang={lang} mobile={isMobile} openDim={openDim}
            labelRef={(id, n) => { rows.current[id] = n; }}
            onEnter={enter} onLeave={leave} onTap={(id) => setOpenDim(o => (o === id ? null : id))} />
          {isMobile && dims.some(d => d.id === openDim) && (
            <div style={{ marginTop: 10, padding: 14, border: `1.5px solid ${LINE}`, borderRadius: 12, background: WHITE }}>
              <DimDetail dim={dims.find(d => d.id === openDim)} score={scores[openDim]} lang={lang} />
            </div>
          )}
        </div>
      ) : <div style={isMobile || !axes ? { display: "flex", flexDirection: "column", gap: 20 } : { display: "grid", gridTemplateColumns: "1fr 240px", gap: 32, alignItems: "start" }}>
        <div>{dims.map((d) => (
          <Bar key={d.id} dim={d} score={scores[d.id]} lang={lang} compact={compact} mobile={isMobile} open={openDim === d.id}
            rowRef={(n) => { rows.current[d.id] = n; }}
            onEnter={() => enter(d.id)} onLeave={() => leave(d.id)} onTap={() => setOpenDim(o => (o === d.id ? null : d.id))} />
        ))}</div>
        {axes && <CultureMap axes={axes} blend={blend} lang={lang} />}
      </div>}
      <HoverCard anchor={openD ? () => rows.current[openD.id] : null} open={!!openD} width={320}
        onMouseEnter={() => openD && enter(openD.id)} onMouseLeave={() => openD && leave(openD.id)}>
        {openD && <DimDetail dim={openD} score={scores[openD.id]} lang={lang} />}
      </HoverCard>
    </div>
  );
};

export default CultureProfile;
